<template>
    <b-card no-body class="mb-4">
        <template v-slot:header>
            <div class="d-flex justify-content-between align-items-center">
                <h5 class="mb-0">
                    {{ $t("Unique products and Serial Numbers") }}
                    <span class="counter" v-if="variant"
                        >({{ variant.total_count }} products,
                        {{ variant.total_count - variant.unique_count }}
                        free)</span
                    >
                </h5>
                <b-button
                    size="sm"
                    variant="primary"
                    class="btn-round"
                    @click="showDialog = true"
                >
                    <font-awesome-icon :icon="['fas', 'plus']" />
                    {{ $t("Add Serial Number") }}
                </b-button>
                <serial-number-dialog
                    @instance-changed="updateList"
                    v-model="showDialog"
                    :variants="variants"
                    :base_product_id="product.id"
                />
            </div>
        </template>
        <b-card-body>
            <b-input-group class="mb-3 search-widget simple-search">
                <template #prepend>
                    <b-input-group-text>
                        <font-awesome-icon icon="search"></font-awesome-icon>
                    </b-input-group-text>
                </template>
                <b-form-input
                    type="search"
                    v-model="filterTerm"
                    :placeholder="$t('search_by_serial_number')"
                />
            </b-input-group>

            <b-table
                class="smartvault-variants-list"
                small
                striped
                hover
                :items="productInstances"
                :fields="fields"
                :current-page="currentPage"
                :per-page="perPage"
                :filter="filterTerm"
                :filter-included-fields="['serial_number']"
            >
                <template v-slot:cell(created_at)="data">
                    {{ formatDate(data.item.created_at) }}
                </template>

                <template v-slot:cell(controls)="data">
                    <b-button
                        size="sm"
                        variant="outline-primary"
                        @click="editVariant(data.item)"
                    >
                        <font-awesome-icon :icon="['fas', 'pen']" />
                        {{ $t("edit") }}
                    </b-button>
                    <b-button
                        size="sm"
                        variant="outline-primary"
                        @click="showCertificatesDialog(data.item)"
                    >
                        <font-awesome-icon icon="list"></font-awesome-icon>
                        {{ $t("Stone Cert") }}
                    </b-button>
                </template>

                <template v-slot:cell(delete)="data">
                    <b-button
                        size="sm"
                        variant="danger"
                        class="btn-lte"
                        @click="showDeleteDialog(data.item)"
                    >
                        <font-awesome-icon :icon="['fas', 'trash']" />
                    </b-button>
                </template>
            </b-table>

            <b-pagination
                v-model="currentPage"
                :per-page="perPage"
                :total-rows="productInstances.length"
                align="right"
                class="my-2"
            />
            
            <serial-number-dialog
                @instance-changed="updateList"
                v-model="dialog.show"
                :variants="variants"
                :base_product_id="dialog.base_product_id"
                :id="dialog.id"
            >
            </serial-number-dialog>
            <certificate-dialog
                v-model="serialDialog.show"
                :serial-number="serialDialog.serialNumber"
                @close="closeModal"
            >
            </certificate-dialog>
        </b-card-body>
    </b-card>
</template>

<script>
import { mapActions, mapState } from "vuex";
import SerialNumberDialog from "../EditDialogs/SerialNumberDialog";
import CertificateDialog from "../EditDialogs/CertificateDialog";
import axios from "axios";

export default {
  name: "SerialNumbers",
  components: { SerialNumberDialog, CertificateDialog },
  props: {
    serialNumbers: Array,
    variant: Object,
    variants: Array
  },
  watch: {
    variant: function(newValue) {
      this.filterInstancesForVariant(newValue);
    }
  },
  data() {
    return {
      filterTerm: "",
      currentPage: 1,
      perPage: 10,
      showDialog: false,
      productInstances: this.serialNumbers,
      serialDialog: {
        show: false,
        serialNumber: null
      },
      dialog: {
        show: false,
        base_product_id: null,
        id: null
      },
      fields: [
        {
          key: "reference",
          sortable: true,
          tdClass: "align-middle"
        },
        {
          key: "serial_number",
          label: this.$t("Serial Number"),
          sortable: true,
          tdClass: "align-middle"
        },
        {
          key: "created_at",
          label: this.$t("date"),
          sortable: true,
          tdClass: "align-middle"
        },
        {
          key: "controls",
          sortable: false
        },
        {
          key: "delete",
          sortable: false
        }
      ]
    };
  },
  computed: {
    ...mapState("smartVaultProduct", {
      product: state => state.product,
      loading: state => state.loading
    })
  },
  mounted() {
    this.filterInstancesForVariant(this.variant);
  },
  methods: {
    ...mapActions("smartVaultProduct", ["getProduct"]),
    editVariant(item) {
      this.dialog = {
        show: true,
        base_product_id: item.base_product_id,
        id: item.id
      };
    },
    formatDate(dateString) {
      if (!dateString) return "";
      const date = new Date(dateString);
      const day = String(date.getDate()).padStart(2, "0");
      const month = String(date.getMonth() + 1).padStart(2, "0"); // Les mois commencent à 0
      const year = date.getFullYear();
      return `${day}-${month}-${year}`;
    },
    showCertificatesDialog(serialNumber) {
      this.serialDialog = {
        show: true,
        serialNumber: serialNumber
      };
    },
    closeModal() {
      this.serialDialog = {
        show: false,
        serialNumber: null
      };
    },
    showDeleteDialog(item) {
      this.$bvModal
        .msgBoxConfirm("Are you sure you want to delete serial number?", {
          title: this.title,
          okVariant: "danger",
          okTitle: this.$t("delete"),
          footerClass: "p-2",
          hideHeaderClose: true
        })
        .then(confirmed => {
          if (confirmed) {
            this.deleteSerialNumber(item);
          }
        });
    },
    updateList(event) {
      if (event.type === "create") {
        for (let i in this.variants) {
          if (this.variants[i].reference === event.model.reference) {
            this.variants[i].unique_count++;
            break;
          }
        }

        this.serialNumbers.push(event.model);

        this.filterInstancesForVariant(this.variant);
      } else {
        const index = this.productInstances.findIndex(
          item => item.id === event.model.id
        );
        if (index !== -1) {
          this.$set(this.productInstances, index, event.model);
        }

        this.filterInstancesForVariant(this.variant);
      }
    },
    deleteSerialNumber(item) {
      this.serialNumbers.splice(this.serialNumbers.indexOf(item), 1);
      this.filterInstancesForVariant(this.variant);
      axios.delete("/api/product-instances/" + item.id).then(() => {
        this.variant.unique_count--;
      });
    },
    filterInstancesForVariant(variant) {
      if (variant !== null) {
        let instances = [];

        for (let i in this.serialNumbers) {
          if (this.serialNumbers[i].reference === variant.reference) {
            instances.push(this.serialNumbers[i]);
          }
        }

        this.productInstances = instances;
      } else {
        this.productInstances = this.serialNumbers;
      }
    }
  }
};
</script>

<style scoped>
</style>
